import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CommonHeader from "../../CommonComponents/CommonHeader";
import { Add, CancelOutlined } from "@mui/icons-material";
import {
  createService,
  deleteService,
  generatePresignedUrl,
  getAllServices,
  updateService,
} from "../../api/service";
import { useEffect, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as Trash } from "../../assets/svg/trash.svg";
import { ReactComponent as Approval } from "../../assets/svg/approval.svg";
import { Eye } from "../../assets/svg";
import CommonDrawer from "../../CommonComponents/CommonDrawer";
import { getAllCategory } from "../../api/category";
import { getApprovalById } from "../../api/approval";
import ChangeRequestViewModal from "../../CommonComponents/ChangeRequestViewModal";
import { useNavigate } from "react-router-dom";
import CommonConfirmationModal from "../../CommonComponents/CommonConfirmationModal";
import SearchInput from "../../CommonComponents/SearchInput";
import ButtonCommon from "../../CommonComponents/ButtonCommon";
import SelectCommon from "../../CommonComponents/SelectCommon";
import ComponentWrapper from "../../CommonComponents/ComponentWrapper";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F9FAFB",
    color: "#000000",
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const ServicesPage = () => {
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [paginatedData, setPaginatedData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [typeValue, setTypeValue] = useState('all');
  const [statusValue, setStatusValue] = useState('all');
  const [filterData, setFilteredData] = useState([])
  const [serviceIcon, setServiceIcon] = useState(null);
  const [storageItems, setStorageItems] = useState([
    {
      name: "",
      description: "",
      duration: "",
      durationValue: "",
    },
  ]);
  const [open, setOpen] = useState(false);
  const [activeStorageItems, setActiveStorageItems] = useState(true);
  const [category, setCategory] = useState([]);
  const [errors, setErrors] = useState({
    name: "",
    category: "",
    description: "",
  });
  const [durationOptions, setDurationOptions] = useState([]);
  const [activeService, setActiveService] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewService, setViewService] = useState({});
  const [formData, setFormData] = useState({});
  const [currentService, setCurrentService] = useState(null);
  const [selectedService, setSelectedService] = useState({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const navigate = useNavigate();
  const [approvalLoader, setApprovalLoader] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [imageEdited, setImageEdited] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [isEdited, setIsEdited] = useState(false);
  const [initialImage, setInitialImage] = useState(null);

  const categoryOptions = (Array.isArray(category) ? category : []).map(
    (category) => ({
      id: category.category_id,
      name: category.category_name,
    })
  );

  useEffect(() => {
    if (currentService && Object.keys(currentService).length > 0) {
      setFormData({
        name: currentService.service_name || "",
        category: currentService.category_id || "",
        description: currentService.description || "",
        isActive: currentService.is_deleted ? false : true
      });
      setServiceIcon({ name: currentService.service_icon });
      setInitialImage({ name: currentService.service_icon })
      setActiveService(currentService.is_deleted ? false : true)
    } else {
      resetForm();
    }
  }, [currentService]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      duration: "",
      durationValue: "",
    });
    setServiceIcon(null);
    setInitialImage(null)
    setErrors({});
  };

  const handleServiceSwitchChange = (e) => {
    setActiveService(e.target.checked);
    if (!e.target.checked) {
      setActiveStorageItems(false); // Disable storage items if the service is turned off
      setStorageItems([]); // Clear storage items when service is inactive
    }
    setIsEdited(true)
  };

  const handleStorageSwitchChange = (e) => {
    setActiveStorageItems(e.target.checked);
    if (!e.target.checked) {
      setStorageItems([]); // Clear all storage items when turned off
    }
  };

  const handleAddStorageItem = () => {
    setStorageItems([
      ...storageItems,
      {
        name: "",
        category: "",
        description: "",
      },
    ]);
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setIsEdited(true)
  };

  const handleRemoveStorageItem = (index) => {
    const updatedStorageItems = storageItems.filter((_, i) => i !== index);
    setStorageItems(updatedStorageItems);
  };

  const handleStorageItemChange = (index, field, value) => {
    const updatedItems = [...storageItems];
    updatedItems[index][field] = value;
    setStorageItems(updatedItems);
  };

  const handleDurationSelect = (e, index) => {
    const selectedDuration = e.target.value;
    const options = getDurationOptions(selectedDuration);
    setDurationOptions(options);
    handleStorageItemChange(index, "duration", selectedDuration);
  };

  const getDurationOptions = (type) => {
    switch (type) {
      case "year":
        return Array.from({ length: 10 }, (_, i) => i + 1); // Years 1-10
      case "month":
        return Array.from({ length: 12 }, (_, i) => i + 1); // Months 1-12
      case "day":
        return Array.from({ length: 31 }, (_, i) => i + 1); // Days 1-31
      case "hour":
        return Array.from({ length: 24 }, (_, i) => i + 1); // Hours 1-24
      default:
        return [];
    }
  };

  const fetchAllCategories = async () => {
    try {
      setCategoryLoading(true);
      const res = await getAllCategory(1, 100);
      if (res?.status_code === 200 || res?.status_code === 404) {
        const filterCategories = res?.data?.categories.filter(categorie => categorie.status === 'active')
        setCategory(filterCategories);
      }
    } catch (err) {
      showErrorToast(err);
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    // if (open) {
    fetchAllCategories();
    // }
    // }, [open]);
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await getAllServices(page, perPage);
      if (response.status_code === 200) {
        const data = response.data.services || [];
        setPaginatedData(data);
        setFilteredData(data)
        setTotalCount(response.data?.total_services || 0);
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setPaginatedData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
    //eslint-disable-next-line
  }, [page, perPage]);

  const validateForm = () => {
    let isValid = true;
    let formErrors = {
      name: "",
      category: "",
      description: "",
      storageItems: "",
    };

    // Validate Service Name
    if (!formData?.name?.trim()) {
      formErrors.name = "Service Name is required.";
      isValid = false;
    }

    // Validate Selected Category
    if (!formData?.category) {
      formErrors.category = "Category selection is required.";
      isValid = false;
    }

    // Validate Service Description
    if (!formData?.description?.trim()) {
      formErrors.description = "Service Description is required.";
      isValid = false;
    }

    // Validate Storage Items only if the switch is on
    if (activeStorageItems && !currentService) {
      storageItems.forEach((item, index) => {
        // Validate Storage Item Name
        if (!item.name.trim()) {
          formErrors[`storageItemName${index}`] =
            "Storage Item name is required.";
          isValid = false;
        } else if (item.name.includes(" ")) {
          formErrors[`storageItemName${index}`] =
            "Storage Item name cannot contain spaces.";
          isValid = false;
        }

        // Validate Storage Item Description
        if (!item.description.trim()) {
          formErrors[`storageItemDescription${index}`] =
            "Storage Item description is required.";
          isValid = false;
        }

        // Validate Duration and Duration Number
        if (!item.duration) {
          formErrors[`storageItemDuration${index}`] =
            "Storage Item Duration is Required";
          isValid = false;
        }

        if (!item.durationNumber) {
          formErrors[`storageItemDurationNumber${index}`] =
            "Storage Item Duration Number is Required.";
          isValid = false;
        } else if (item.durationNumber <= 0) {
          formErrors[`storageItemDurationNumber${index}`] =
            "Duration number must be greater than zero.";
          isValid = false;
        }
      });
    }
    setErrors(formErrors);
    return isValid;
  };


  const formatStorageItemsData = () => {
    return storageItems.map((item) => ({
      name: item.name,
      description: item.description,
      duration: `${item.durationNumber} ${item.duration}`,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Always at the top

    if (!serviceIcon) {
      setImageError('Image is required')
      return
    } else {
      setImageError(null)
    }

    if (!validateForm()) {
      return;
    }

    setSubmitLoading(true);

    try {
      // Handle file upload if a service icon was selected
      let iconFileName = currentService?.service_icon || "";

      if (initialImage?.name !== serviceIcon?.name) {
        const presignedUrlResponse = await generatePresignedUrl(serviceIcon.name);

        if (presignedUrlResponse?.status_code !== 200) {
          throw new Error(
            presignedUrlResponse?.message || "Failed to generate upload URL"
          );
        }

        const { url, file_name } = presignedUrlResponse.data;
        iconFileName = file_name;

        const uploadResponse = await fetch(url, {
          method: "PUT",
          body: serviceIcon,
          headers: {
            "Content-Type": serviceIcon.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `File upload failed with status: ${uploadResponse.status}`
          );
        }
      }
      //  else {
      //   console.log('else comn=nsole');

      //   const presignedUrlResponse = await generatePresignedUrl(serviceIcon.name);

      //   if (presignedUrlResponse?.status_code !== 200) {
      //     throw new Error(
      //       presignedUrlResponse?.message || "Failed to generate upload URL"
      //     );
      //   }

      //   const { url, file_name } = presignedUrlResponse.data;
      //   iconFileName = file_name;

      //   const uploadResponse = await fetch(url, {
      //     method: "PUT",
      //     body: serviceIcon,
      //     headers: {
      //       "Content-Type": serviceIcon.type,
      //     },
      //   });

      //   if (!uploadResponse.ok) {
      //     throw new Error(
      //       `File upload failed with status: ${uploadResponse.status}`
      //     );
      //   }
      // }

      // Step 3: Create or update service with the file name
      const payload = {
        service_icon: iconFileName,
        service_name: formData.name,
        description: formData.description,
        category_id: formData.category,
        is_deleted: !activeService,
        // ...(Object.keys(currentService).length === 0
        ...(!currentService
          ? { storage_item: formatStorageItemsData() }
          : {}),
      };

      let response;
      // if (Object.keys(currentService).length > 0) {
      if (currentService) {
        response = await updateService(currentService.service_id, payload);
      } else {
        response = await createService(payload);
      }

      if (response?.status_code === 201) {
        const res = await getAllServices(page, perPage);

        if (res?.status_code === 200 || res?.status_code === 404) {
          setPaginatedData(res?.data?.services || []);
        }
        showSuccessToast(response.message);
        setOpen(false);
      } else {
        showErrorToast(response.message);
      }

    } catch (error) {
      console.error("Service submit error:", error);
      showErrorToast(
        error.message || "There was an error submitting the form."
      );
    } finally {
      setSubmitLoading(false);
      setImageEdited(false)
      setIsEdited(false)
    }
  };

  // Add view handler
  const handleViewClick = async (approvalId) => {
    console.log(approvalId);

    setApprovalLoader(approvalId);
    try {
      // setLoading(true);
      const response = await getApprovalById(approvalId);

      if (response.success) {
        const approvalData = response.data;

        setViewService({
          ...approvalData,
        });
        // setLoading(false);
        setViewModalOpen(true);
      } else {
        console.error("No data received for service approval.");
        showErrorToast(response.message)
        // setLoading(false);
      }
    } catch (error) {
      // setLoading(false);
      console.log(error);
      showErrorToast(error.message || "Error fetching service approval details");
    } finally {
      setApprovalLoader(null);
    }
  };

  const handleEditService = (service) => {
    setCurrentService(service);
    setOpen(true);
  };

  const handleDeleteService = async (serviceId) => {
    try {
      const response = await deleteService(serviceId.service_id);

      if (response.success) {
        showSuccessToast(response.message);
        fetchServices(); // Refresh services list
      } else {
        showErrorToast(response.message);
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      showErrorToast(error.message || "Something went wrong while deleting service.");
    }
  };

  useEffect(() => {
    const data = paginatedData?.length ? paginatedData : [];

    const filtered = data.filter((item) => {
      const search = searchValue?.toLowerCase() || '';
      const service = item.service_name?.toLowerCase() || '';
      const isNameMatch = service.includes(search);

      const isTypeMatch = typeValue === 'all'
        ? true
        : item.category_type?.toLowerCase() === typeValue?.toLowerCase();

      const isStatusMatch = statusValue === 'all'
        ? true
        : item.status?.toLowerCase() === statusValue?.toLowerCase();

      return isNameMatch && isStatusMatch && isTypeMatch;
    });

    setFilteredData(filtered);
  }, [searchValue, statusValue, typeValue, paginatedData]);

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: "1000px" }}> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', mb: 3 }}>
        <CommonHeader
          title="Master Services"
          subTitle="You can review, audit and change the status of master service on this page."
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'end', flexDirection: 'row', mb: 2 }}>
          {/* <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Service Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "#fff",
              borderRadius: 1,
              width: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
            }}
          /> */}
          <SearchInput
            placeholder="Search by Service Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <SelectCommon
            label="Category"
            value={typeValue}
            onChange={(e) => setTypeValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Optional", value: "optional" },
              { label: "Required", value: "required" },
            ]}
          />
          <SelectCommon
            label="Status"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
              { label: "Draft", value: "draft" },
            ]}
          />
          <ButtonCommon icon={<Add />} onClick={() => {
            setOpen(true)
          }}>
            New Service
          </ButtonCommon>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "12px" }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Service Name</StyledTableCell>
              <StyledTableCell>Category</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Storage Item</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filterData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No data requests found.
                </TableCell>
              </TableRow>
            ) : (
              filterData.map((row) => {
                return (
                  <TableRow key={row.service_id} hover>
                    <StyledTableCell>{row.service_name}</StyledTableCell>
                    <StyledTableCell>{row.category_type}</StyledTableCell>
                    <StyledTableCell>
                      {row?.description?.length > 40
                        ? `${row.description.slice(0, 40)}...`
                        : row.description}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.storage_item?.length}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        textTransform: "capitalize",
                        color:
                          row.status === "active"
                            ? "#00B894"
                            : row.status === "inactive"
                              ? "#FF946D"
                              : "#6994FF",
                        fontWeight: 600,
                      }}
                    >
                      {row.status}
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: '140px' }}>
                      <Tooltip
                        title={row.status === "draft" ? "Approval" : "View"}
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{
                            color: "inherit",
                          }}
                          onClick={() =>
                            row.status === "draft"
                              ? handleViewClick(row.approval_data)
                              : navigate(`/master/services/service-details/${row.service_id}`)
                          }
                        >
                          {row.status === "draft" ?
                            approvalLoader === row.approval_data ?
                              <CircularProgress size={20} /> : <Approval />
                            : <Eye />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleEditService(row)}
                          disabled={row.status === "draft" || row.approval_id}
                          sx={{
                            color: (theme) =>
                              row.status === "draft"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setOpenDeleteModal(true);
                            setSelectedService(row);
                          }}
                          disabled={row.status === "draft" || row.status === "inactive" || row.approval_id}
                          sx={{
                            color: (theme) =>
                              row.status === "draft"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                        >
                          <Trash />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(totalCount / perPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          shape="rounded"
          variant="outlined"
        />
      </Box>

      <CommonConfirmationModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Delete Service"
        description={`Are you sure you want to delete the service "${selectedService?.service_name}"? This action cannot be undone.`}
        onConfirm={() => {
          handleDeleteService(selectedService);
          setOpenDeleteModal(false);
        }}
        loading={loading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
      <CommonDrawer
        open={open}
        onClose={() => {
          setOpen(false);
          setCurrentService(null);
          setImageEdited(false)
          setIsEdited(false)
        }}
        title={
          currentService
            ? "Update Service"
            : "Create Service"
        }
        subTitle="Sort cookies with specific jobs, helping users personalize their cookie choices."
        primaryButton={{
          label: submitLoading ? (
            <CircularProgress size={20} />
          ) : currentService ? (
            "Update"
          ) : (
            "Create"
          ),
          onClick: handleSubmit,
          sx: { px: 2 },
          disabled: !isEdited
        }}
        cancelButtonLabel="Cancel"
        drawerWidth={400}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {/* Default Settings Section */}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#525455", mb: 2 }}
          >
            Default Settings
          </Typography>

          <Box sx={{ mb: 3 }}>
            <InputLabel
              required
              sx={{
                color: "#525455",
                mb: 1,
                fontSize: "0.875rem",
                "& .MuiFormLabel-asterisk": {
                  color: "red",
                },
              }}
            >
              Service Icon (PNG, JPG, SVG)
            </InputLabel>
            <Box>
              <Button
                variant="outlined"
                component="label"
                sx={{ textTransform: "none" }}
              >
                Upload
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const img = new Image();
                    const objectUrl = URL.createObjectURL(file);

                    img.onload = () => {
                      if (img.width > 40 || img.height > 40) {
                        setImageError("Image must be 40x40 pixels or smaller");
                        setServiceIcon(null);
                        setImageEdited(false);
                        setIsEdited(false);
                      } else {
                        setServiceIcon(file);
                        setImageEdited(true);
                        setImageError(null);
                        setIsEdited(true);
                      }
                      URL.revokeObjectURL(objectUrl); // cleanup
                    };

                    img.onerror = () => {
                      setImageError("Invalid image file.");
                      setServiceIcon(null);
                      URL.revokeObjectURL(objectUrl); // cleanup
                    };

                    img.src = objectUrl;
                  }}
                />
              </Button>
              <FormHelperText error>{imageError}</FormHelperText>
            </Box>
            {serviceIcon && (
              <Stack sx={{ marginTop: 1 }}>
                <Box
                  component="img"
                  src={
                    imageEdited
                      ? URL.createObjectURL(serviceIcon)
                      : `${process.env.REACT_APP_SERVICE_ICON_URL}/${serviceIcon.name}`
                  }
                  sx={{ width: '50px' }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Selected: {serviceIcon.name}
                </Typography>
              </Stack>
            )}

          </Box>

          {/* Service Name Field */}
          <Box sx={{ mb: 3 }}>
            <InputLabel
              required
              sx={{
                color: "#525455",
                mb: 1,
                fontSize: "0.875rem",
                "& .MuiFormLabel-asterisk": {
                  color: "red",
                },
              }}
            >
              Name
            </InputLabel>
            <TextField
              fullWidth
              name="name"
              size="small"
              placeholder="Google Analytics"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              sx={{ mb: 0.5 }}
              error={!!errors.name}
              helperText={errors.name}
            />
            <Typography variant="caption" color="text.secondary">
              You can add * to pattern match & capture all items that follow
              this pattern.
            </Typography>
          </Box>

          {/* Category Selection */}
          <Box sx={{ mb: 3 }}>
            <InputLabel
              required
              sx={{
                color: "#525455",
                mb: 1,
                fontSize: "0.875rem",
                "& .MuiFormLabel-asterisk": {
                  color: "red",
                },
              }}
            >
              Select Category
            </InputLabel>
            <Select
              value={formData.category}
              onChange={(e) => handleChange("category", e.target.value)}
              fullWidth
              size="small"
              error={!!errors.category}
            >
              {categoryLoading ?
                <MenuItem sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={20} /></MenuItem> :
                categoryOptions && categoryOptions.length > 0 && categoryOptions.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
            </Select>
            <FormHelperText error>{errors.category}</FormHelperText>
          </Box>

          {/* Service Description */}
          <Box sx={{ mb: 4 }}>
            <InputLabel
              required
              sx={{
                color: "#525455",
                mb: 1,
                fontSize: "0.875rem",
                "& .MuiFormLabel-asterisk": {
                  color: "red",
                },
              }}
            >
              Description of Service
            </InputLabel>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Description"
              error={!!errors.description}
              helperText={errors.description}
            />
          </Box>

          {/* Storage Items Section */}
          {/* {Object.keys(currentService)?.length === 0 && ( */}
          {!currentService && (
            <Box sx={{ mb: 4, bgcolor: "#F8F8F8", p: 2, borderRadius: "12px" }}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex" flexDirection="column">
                  <Typography color="#5F5F5F" fontWeight="600">
                    Storage Items
                  </Typography>
                  <Typography variant="body2" color="#5F5F5F">
                    Enable the service by default.
                  </Typography>
                </Box>
                <Switch
                  color="primary"
                  checked={activeStorageItems}
                  onChange={handleStorageSwitchChange}
                  disabled={!activeService} // Disable the storage switch if the service is inactive
                />
              </Box>

              {/* Only show and enable storage items if service is active */}
              {activeStorageItems && activeService ? (
                <>
                  {storageItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ mb: 2, p: 2, bgcolor: "#FFF", borderRadius: 2 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#525455" }}
                        >
                          Storage Item Name
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveStorageItem(index)}
                        >
                          <CancelOutlined size={12} />
                        </IconButton>
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Storage Item"
                        value={item.name}
                        onChange={(e) =>
                          handleStorageItemChange(index, "name", e.target.value)
                        }
                        sx={{ mb: 2 }}
                        error={!!errors[`storageItemName${index}`]} // Error condition
                        helperText={errors[`storageItemName${index}`]} // Error message
                      />
                      {/* Description */}
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={item.description}
                        onChange={(e) =>
                          handleStorageItemChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        placeholder="Description here"
                        sx={{ mb: 2 }}
                        error={!!errors[`storageItemDescription${index}`]} // Error condition
                        helperText={errors[`storageItemDescription${index}`]} // Error message
                      />

                      {/* Duration */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexDirection: "column",
                        }}
                      >
                        <Select
                          size="small"
                          value={item.duration}
                          onChange={(e) => handleDurationSelect(e, index)}
                          sx={{ flex: 1 }}
                          error={!!errors[`storageItemDuration${index}`]} // Error condition
                        >
                          <MenuItem value="year">Year</MenuItem>
                          <MenuItem value="month">Month</MenuItem>
                          <MenuItem value="day">Day</MenuItem>
                          <MenuItem value="hour">Hour</MenuItem>
                        </Select>
                        {errors[`storageItemDuration${index}`] && (
                          <FormHelperText error>
                            {errors[`storageItemDuration${index}`]}
                          </FormHelperText>
                        )}
                        <Select
                          size="small"
                          value={item.durationNumber}
                          onChange={(e) =>
                            handleStorageItemChange(
                              index,
                              "durationNumber",
                              e.target.value
                            )
                          }
                          sx={{ flex: 1 }}
                          error={!!errors[`storageItemDurationNumber${index}`]} // Error condition
                        >
                          {durationOptions.length > 0 ? (
                            durationOptions.map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="">Select Duration Number</MenuItem>
                          )}
                        </Select>
                        {errors[`storageItemDurationNumber${index}`] && (
                          <FormHelperText error>
                            {errors[`storageItemDurationNumber${index}`]}
                          </FormHelperText>
                        )}
                      </Box>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Storage items are disabled because the service is not active.
                </Typography>
              )}

              {activeStorageItems && (
                <Button
                  startIcon={<Add size={16} />}
                  onClick={handleAddStorageItem}
                  sx={{
                    color: "#525455",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Add More
                </Button>
              )}
              <Divider sx={{ mt: 2, mb: 2 }} />

              {/* Service Active */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#525455" }}
                  >
                    Service Active
                  </Typography>
                  <Switch
                    color="primary"
                    checked={activeService}
                    onChange={handleServiceSwitchChange}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  If disabled, this service will not be shown to any visitors.
                </Typography>
              </Box>
            </Box>
          )}

          {currentService && Object.keys(currentService).length > 0 && (
            <>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#525455" }}
                  >
                    Activate Service
                  </Typography>
                  <Switch
                    color="primary"
                    checked={activeService}
                    onChange={handleServiceSwitchChange}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  If disabled, this service will not be shown to any visitors.
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </CommonDrawer>
      <ChangeRequestViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fetchData={fetchServices}
        title={`Service ${viewService?.action_type} request`}
        approvalId={viewService?.approval_id}
        beforeData={viewService?.data_before ? {
          icon: viewService?.data_before?.service_icon,
          active: viewService?.data_before?.is_active,
          name: viewService?.data_before?.service_name,
          description: viewService?.data_before?.description,
          storage_item: viewService?.data_before?.storage_item,
          status: viewService?.data_before?.is_deleted ? 'Inactive' : 'Active'
        } : null}
        afterData={{
          icon: viewService?.data_after?.service_icon,
          active: viewService?.data_after?.is_active,
          name: viewService?.data_after?.service_name,
          description: viewService?.data_after?.description,
          storage_item: viewService?.data_after?.storage_item,
          status: viewService?.data_after?.is_deleted ? 'Inactive' : 'Active'
        }}
        requestedBy={viewService?.requested_by}
        requestedAt={viewService?.requested_at}
        actionType={viewService?.action_type}
      // onApprove={() => handleApproveLanguage(viewService)}
      // onReject={() => handleRejectLanguage(viewService)}
      />
      {/* </Stack> */}
    </ComponentWrapper>
  );
};
export default ServicesPage;
